// Ops → Analytics: the company-wide operations dashboard — the funnel a business runs on (leads opened →
// in progress → completed / abandoned), the AI sentiment mix, and a per-flow breakdown with each flow's
// top drop-off point. Tenant-aware (super-admin can switch company). Data via ecapApi.getAnalytics.
import React, { useCallback, useEffect, useState } from 'react';
import { PageHeader, Alert, Button } from '../ui/primitives';
import { getAnalytics, getFlowBreakdown, activeTenant, getAiUsage } from '../services/ecapApi';
import CapabilityNotice from '../components/CapabilityNotice';

const pct = (x) => `${Math.round((x || 0) * 100)}%`;
const fmt = (n) => (n || 0).toLocaleString();

function AiStat({ label, value, tone }) {
  return (
    <div>
      <div className={`text-xl font-semibold ${tone || 'text-slate-800'}`}>{value}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );
}

function UsageTable({ title, rows }) {
  const top = (rows || []).slice(0, 6);
  return (
    <div>
      <div className="text-[11px] font-semibold text-slate-400 mb-1">{title}</div>
      <table className="w-full text-xs">
        <tbody>
          {top.length === 0 && <tr><td className="text-slate-300 py-1">—</td></tr>}
          {top.map((r) => (
            <tr key={r.key} className="border-t border-slate-50">
              <td className="py-1 text-slate-600 truncate max-w-[120px]" title={r.key}>{r.key}</td>
              <td className="py-1 text-right text-slate-400">{fmt(r.inputTokens + r.outputTokens)} tok</td>
              <td className="py-1 text-right text-indigo-600 font-medium">${(r.cost || 0).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OpsAnalytics({ authSession, recordSources }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tenant] = useState(activeTenant()); // fixed per mount; the shell remounts this page on company switch
  const [openFlow, setOpenFlow] = useState(null); // flowId whose attribute breakdown is expanded
  const [ai, setAi] = useState(null); // AI token/cost usage (last 30 days)
  const [aiErr, setAiErr] = useState(null); // OPSAN-2: surface a usage-load failure instead of silently hiding the panel
  useEffect(() => {
    let alive = true;
    setAiErr(null);
    getAiUsage(30, authSession).then((r) => { if (alive) setAi(r); }).catch((e) => { if (alive) setAiErr(e?.message || 'Could not load AI usage.'); });
    return () => { alive = false; };
  }, [authSession, tenant]);

  const load = useCallback(() => {
    getAnalytics(authSession).then(setData).catch((e) => setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authSession, tenant]);
  useEffect(() => { load(); }, [load]);

  const d = data || {};
  const cards = [
    ['Leads opened', d.totalLeads, 'text-slate-800'],
    ['In progress', d.active, 'text-blue-600'],
    ['Completed', d.completed, 'text-emerald-600'],
    ['Abandoned', d.abandoned, 'text-slate-400'],
    ['Completion', pct(d.completionRate), 'text-indigo-600'],
    ['LLM tokens', d.totalTokens, 'text-slate-800'],
  ];
  const sent = d.sentiment || {};
  const sentTotal = Object.values(sent).reduce((a, b) => a + b, 0);
  const sentTone = { POSITIVE: 'bg-emerald-500', NEUTRAL: 'bg-slate-300', NEGATIVE: 'bg-rose-500' };

  return (
    <div className="p-6">
      <PageHeader title="Analytics" subtitle="Company operations — the lead funnel across every flow"
        right={<Button size="sm" variant="ghost" onClick={load}>Refresh</Button>} />
      <CapabilityNotice capability={recordSources} kind="analytics" />
      {error && <Alert tone="danger" className="mb-3">{error}</Alert>}

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5">
        {cards.map(([label, val, tone]) => (
          <div key={label} className="rounded-xl border border-slate-200 bg-white p-3">
            <div className={`text-2xl font-semibold ${tone}`}>{val ?? '—'}</div>
            <div className="text-xs text-slate-500">{label}</div>
          </div>
        ))}
      </div>

      {sentTotal > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 mb-5 max-w-2xl">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Sentiment of completed conversations</div>
          <div className="flex h-3 rounded-full overflow-hidden mb-2">
            {['POSITIVE', 'NEUTRAL', 'NEGATIVE'].map((k) => sent[k] ? (
              <div key={k} className={sentTone[k]} style={{ width: `${(sent[k] / sentTotal) * 100}%` }} title={`${k}: ${sent[k]}`} />
            ) : null)}
          </div>
          <div className="flex gap-4 text-xs text-slate-500">
            {['POSITIVE', 'NEUTRAL', 'NEGATIVE'].map((k) => <span key={k}><span className={`inline-block w-2 h-2 rounded-full ${sentTone[k]} mr-1`} />{k.toLowerCase()} {sent[k] || 0}</span>)}
          </div>
        </div>
      )}

      {aiErr && <div className="text-[11px] text-amber-600 mb-3">⚠ AI cost &amp; tokens unavailable ({aiErr}).</div>}
      {ai && ai.calls > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 mb-5">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">AI cost &amp; tokens — last 30 days</div>
          <div className="flex flex-wrap gap-6 mb-3">
            <AiStat label="Est. cost" value={`$${(ai.estimatedCost || 0).toFixed(2)}`} tone="text-indigo-600" />
            <AiStat label="Calls" value={fmt(ai.calls)} />
            <AiStat label="Input tokens" value={fmt(ai.inputTokens)} />
            <AiStat label="Output tokens" value={fmt(ai.outputTokens)} />
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <UsageTable title="By flow" rows={ai.byFlow} />
            <UsageTable title="By model" rows={ai.byModel} />
            <UsageTable title="By call type" rows={ai.byType} />
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Flow</th>
              <th className="text-right px-3 py-2 font-medium">Leads</th>
              <th className="text-right px-3 py-2 font-medium">Completed</th>
              <th className="text-right px-3 py-2 font-medium">Abandoned</th>
              <th className="text-right px-3 py-2 font-medium">Completion</th>
              <th className="text-right px-3 py-2 font-medium">Avg latency</th>
              <th className="text-left px-3 py-2 font-medium">Top drop-off</th>
            </tr>
          </thead>
          <tbody>
            {(d.flows || []).map((f) => (
              <React.Fragment key={f.flowId}>
                <tr className="border-t border-slate-100 cursor-pointer hover:bg-slate-50" onClick={() => setOpenFlow(openFlow === f.flowId ? null : f.flowId)}>
                  <td className="px-3 py-2 font-medium text-slate-800">
                    <span className="text-slate-400 mr-1">{openFlow === f.flowId ? '▾' : '▸'}</span>{f.name || f.flowId}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-700">{f.total}</td>
                  <td className="px-3 py-2 text-right text-emerald-600">{f.completed}</td>
                  <td className="px-3 py-2 text-right text-slate-400">{f.abandoned}</td>
                  <td className="px-3 py-2 text-right">{pct(f.completionRate)}</td>
                  <td className="px-3 py-2 text-right text-slate-500">{Math.round(f.avgLatencyMs)}ms</td>
                  <td className="px-3 py-2 text-slate-500">{f.topDropOff || <span className="text-slate-300">—</span>}</td>
                </tr>
                {openFlow === f.flowId && (
                  <tr className="bg-slate-50/60"><td colSpan={7} className="px-3 py-3">
                    <FlowBreakdown flowId={f.flowId} authSession={authSession} />
                  </td></tr>
                )}
              </React.Fragment>
            ))}
            {(!d.flows || d.flows.length === 0) && <tr><td colSpan={7} className="px-3 py-3 text-slate-400">No flows / activity yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Flow-specific operational breakdown, inline under a flow row: group its leads by any captured
// attribute (driver / rating / issue type). Reuses the same endpoint as the Leads page's 📊 Breakdown.
function FlowBreakdown({ flowId, authSession }) {
  const [data, setData] = useState(null);
  const [attr, setAttr] = useState('');
  const [err, setErr] = useState(null); // OPSAN-1: distinguish a load failure from a genuinely-empty breakdown
  useEffect(() => {
    let alive = true;
    setErr(null);
    getFlowBreakdown(flowId, authSession).then((d) => { if (alive) { setData(d); setAttr(d?.attributes?.[0]?.attr || ''); } })
      .catch((e) => { if (alive) setErr(e?.message || 'Could not load the breakdown.'); });
    return () => { alive = false; };
  }, [flowId, authSession]);
  if (err) return <div className="text-xs text-rose-600">⚠ Could not load the breakdown ({err}). Retry by collapsing and reopening this flow.</div>;
  if (!data) return <div className="text-xs text-slate-400">Loading breakdown…</div>;
  const attrs = data.attributes || [];
  if (attrs.length === 0) return <div className="text-xs text-slate-400">No captured attributes to break down yet.</div>;
  const current = attrs.find((a) => a.attr === attr) || attrs[0];
  const max = Math.max(1, ...(current?.values || []).map((v) => v.count));
  return (
    <div>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="text-[11px] text-slate-500">📊 group {data.totalLeads} leads by</span>
        <select className="rounded-lg border border-slate-200 px-2 py-1 text-sm" value={attr} onChange={(e) => setAttr(e.target.value)}>
          {attrs.map((a) => <option key={a.attr} value={a.attr}>{a.attr} ({a.distinctValues})</option>)}
        </select>
      </div>
      <div className="space-y-1 max-w-2xl">
        {(current?.values || []).map((v) => (
          <div key={v.value} className="flex items-center gap-2 text-sm">
            <span className="w-40 truncate text-slate-600" title={v.value}>{v.value}</span>
            <div className="flex-1 bg-slate-100 rounded h-4 overflow-hidden"><div className="h-4 bg-blue-500/80 rounded" style={{ width: `${Math.round((v.count / max) * 100)}%` }} /></div>
            <span className="w-8 text-right text-slate-500 tabular-nums">{v.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default OpsAnalytics;
